<template>
  <div class="report-wrapper fade-in">

    <!-- ===== LOADER ===== -->
    <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>Loading...</p>
      </div>
    </transition>

    <!-- ===============================
         HERO HEADER SECTION
    =============================== -->
    <div class="report-hero">
      <div class="hero-left">
        <h1 style="color: white;">
          <i class="bx bx-file"></i>
          {{ reportTitle }}
        </h1>
        <p class="hero-subtitle" style="color: white;">
          A detailed summary and intelligent insights about your meeting.
        </p>

        <div class="hero-badges">
          <span class="badge">
            <i class="bx bx-calendar-event"></i>
            {{ appointment.date_fmt }}
          </span>

          <span class="badge">
            <i class="bx bx-time-five"></i>
            {{ appointment.slot }}
          </span>

          <span class="badge">
            <i class="bx bx-user"></i>
            {{ appointment.name }}
          </span>

          <span class="badge">
            <i class="bx bx-credit-card"></i>
            ${{ appointment.amount }}
          </span>
        </div>
      </div>

      <div class="hero-right">
        <button class="btn-dark" @click="exportPDF">
          <i class="bx bx-download"></i> Export PDF
        </button>
      </div>
    </div>

    <!-- ===============================
         MAIN GRID LAYOUT
    =============================== -->
    <div class="report-grid">

      <!-- LEFT SIDE — MAIN REPORT CONTENT -->
      <div class="left-panel">

        <!-- Summary -->
        <div class="card">
          <div class="card-title">
            <i class="bx bx-detail"></i>
            Meeting Summary
          </div>
          <p class="card-text">{{ report.summary }}</p>
        </div>

        <!-- Key Points -->
        <div class="card">
          <div class="card-title">
            <i class="bx bx-list-check"></i>
            Key Discussion Points
          </div>

          <div class="timeline">
            <div
              class="timeline-item"
              v-for="(p, i) in report.points"
              :key="i"
            >
              <span class="dot"></span>
              <p>{{ p }}</p>
            </div>

            <p v-if="!report.points.length" class="txt-muted">
              No discussion points added.
            </p>
          </div>
        </div>

        <!-- Action Items -->
        <div class="card">
          <div class="card-title">
            <i class="bx bx-task"></i>
            Action Items
          </div>

          <ul class="action-list">
            <li v-for="(a, i) in report.actions" :key="i">
              <i class="bx bx-check-circle"></i>
              <span>{{ a }}</span>
            </li>
          </ul>

          <p v-if="!report.actions.length" class="txt-muted">No action items added.</p>
        </div>

        <!-- Follow Up -->
        <div class="card">
          <div class="card-title">
            <i class="bx bx-refresh"></i>
            Follow-up Plan
          </div>
          <p class="card-text">{{ report.followup }}</p>
        </div>

      </div>

      <!-- RIGHT SIDE — DETAILS -->
      <div class="right-panel">

        <!-- Appointment Details -->
        <div class="side-card">
          <div class="side-title"><i class="bx bx-info-circle"></i> Appointment Details</div>
          <div class="info-row"><span>Date</span> <b>{{ appointment.date_fmt }}</b></div>
          <div class="info-row"><span>Time</span> <b>{{ appointment.slot }}</b></div>
          <div class="info-row"><span>Mode</span> <b>{{ appointment.mode }}</b></div>
          <div class="info-row"><span>Package</span> <b>{{ appointment.package }}</b></div>
        </div>

        <div class="side-card">
          <div class="side-title"><i class="bx bx-user-circle"></i> Client Info</div>
          <div class="info-row"><span>Name</span> <b>{{ appointment.name }}</b></div>
          <div class="info-row"><span>Email</span> <b>{{ appointment.email }}</b></div>
          <div class="info-row"><span>Mobile</span> <b>{{ appointment.mobile }}</b></div>
        </div>

        <div class="side-card">
          <div class="side-title"><i class="bx bx-wallet"></i> Payment</div>
          <div class="info-row"><span>Amount</span> <b>${{ appointment.amount }}</b></div>
          <div class="info-row"><span>Status</span> <b>{{ appointment.payment_status }}</b></div>
        </div>

        <div class="side-card sentiment-card">
          <div class="side-title">
            <i class="bx bx-happy-heart-eyes"></i>
            AI Sentiment Summary
          </div>
          <p class="sentiment-text">{{ report.sentiment }}</p>
        </div>

      </div>
    </div>
  </div>
</template>
<script>
import { getAppointmentById } from "@/apis/bookapis";
import { useToast } from "vue-toastification";

export default {
  name: "AppointmentReport",

  data() {
    return {
      loading: false,
      reportTitle: "",
      appointmentId: "",
      appointment: {},
      report: {
        summary: "",
        points: [],
        actions: [],
        followup: "",
        sentiment: ""
      }
    };
  },

  async mounted() {
    this.appointmentId = this.$route.params.booking_page_id;
    await this.loadReport();
  },

  methods: {
    async loadReport() {
  try {
    this.loading = true;

    // Get appointment ID from route
    const appointment_id = this.$route.params.booking_page_id;

    const res = await getAppointmentById(appointment_id);

    if (!res.status) {
      useToast().error(res.success);
      return;
    }

    const a = res.appointment;

    // ---- Set Title ----
    this.reportTitle = `${a.appointment_page_title} — ${this.formatDate(a.appointment_date)}`;

    // ---- Set Appointment Data ----
    this.appointment = {
      date_fmt: this.formatDate(a.appointment_date),
      slot: a.appointment_slot,
      name: a.appointment_name,
      mode: a.appointment_mode || "No Location",
      email: a.appointment_email,
      mobile: a.appointment_mobile,
      amount: a.appointment_amt,
      package: a.appointment_package,
      payment_status: a.appointment_payment_status
    };

    // ---- FIX: Ensure report always exists ----
    this.report = res.report || {
      summary: "",
      points: [],
      actions: [],
      followup: "",
      sentiment: ""
    };

  } catch (err) {
    console.error(err);
  } finally {
    this.loading = false;
  }
}
,

    formatDate(date) {
      const d = new Date(date);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    },

    exportPDF() {
      useToast().info("PDF download coming soon.");
    }
  }
};
</script>
<style scoped>

/* Wrapper */
.report-wrapper {
  padding: 20px 20px 40px;
}

/* ===============================
   HERO HEADER
=================================*/
.report-hero {
  background: linear-gradient(135deg, #111827, #1f2937, #374151);
  color: #fff;
  padding: 28px;
  border-radius: 14px;
  margin-bottom: 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.report-hero h1 {
  font-size: 1.7rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.hero-subtitle {
  margin-top: 4px;
  font-size: 0.95rem;
  opacity: 0.85;
}

.hero-badges {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.badge {
  background: rgba(255,255,255,0.1);
  padding: 6px 12px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
}

.btn-dark {
  background: #fff;
  color: #111827;
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.btn-dark:hover {
  background: #e5e7eb;
  transform: translateY(-2px);
}

/* ===============================
   MAIN GRID
=================================*/
.report-grid {
  display: grid;
  grid-template-columns: 68% 30%;
  gap: 24px;
}

/* LEFT SIDE */
.left-panel .card {
  background: var(--card);
  border: 1px solid var(--border);
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 1px 5px rgba(0,0,0,0.06);
}

.card-title {
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-text {
  line-height: 1.6;
  color: var(--text);
}

/* Timeline (Discussion Points) */
.timeline {
  margin-top: 12px;
}

.timeline-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
}

.timeline-item .dot {
  width: 10px;
  height: 10px;
  background: var(--primary);
  border-radius: 50%;
  margin-top: 6px;
}

.action-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.action-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}

/* ===============================
   RIGHT PANEL
=================================*/
.side-card {
  background: var(--card);
  border: 1px solid var(--border);
  padding: 18px;
  border-radius: 12px;
  margin-bottom: 20px;
}

.side-title {
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px dashed var(--border);
}

/* Sentiment */
.sentiment-card {
  background: #f0fdf4;
  border-color: #86efac !important;
}

.sentiment-text {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #166534;
  font-weight: 500;
}

.txt-muted {
  color: var(--muted);
}

/* Responsive */
@media(max-width: 900px) {
  .report-grid {
    grid-template-columns: 1fr;
  }
}
</style>
